import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Gift, 
    Heart, 
    MessageCircle, 
    Star, 
    Users, 
    Sparkles,
    ArrowRight,
    Check,
    Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { useAuth } from '../contexts/AuthContext';

const PROMO_CODE = "COREGULATER";
const PROMO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/3dca5804-461a-4919-9a13-70c20d6d472a/images/bf06acc76027167325516cf6046b6526ca729367ee98f4da3c9a78f859d09774.png";

const PromoLanding = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [copied, setCopied] = useState(false);

    const handleCopyCode = () => {
        navigator.clipboard.writeText(PROMO_CODE);
        setCopied(true);
        toast.success('Code copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleGetStarted = () => {
        if (user) {
            navigate('/account');
        } else {
            navigate('/signup');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#F9F7F4] to-white">
            {/* Hero Section with Background Image */}
            <div 
                className="relative min-h-[60vh] flex items-center justify-center px-6 py-16"
                style={{
                    backgroundImage: `url(${PROMO_IMAGE})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                {/* Overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent" />
                
                <div className="relative z-10 max-w-2xl">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="px-3 py-1 rounded-full bg-[#1D6B6B]/10 border border-[#1D6B6B]/20">
                            <SmallText className="text-[#1D6B6B] font-medium">Community Invite</SmallText>
                        </div>
                    </div>
                    
                    <Heading as="h1" className="text-4xl md:text-5xl text-[#1D6B6B] mb-4 leading-tight">
                        Help Shape the Future of ADHD Regulation
                    </Heading>
                    
                    <BodyText className="text-xl text-[#344648] mb-8 leading-relaxed">
                        You're invited to be a <strong>CoRegulater</strong> — a founding member of our community helping build tools that actually work for ADHD minds.
                    </BodyText>

                    {/* Promo Code Card */}
                    <div className="glass-card rounded-2xl p-6 mb-6 max-w-md">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFBB98] to-[#E09060] flex items-center justify-center">
                                <Gift className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <Heading level={4} className="text-[#1D6B6B]">Your Exclusive Code</Heading>
                                <SmallText className="text-[#7D8E95]">14 days free premium access</SmallText>
                            </div>
                        </div>

                        <button
                            onClick={handleCopyCode}
                            className="w-full flex items-center justify-between p-4 rounded-xl bg-[#1D6B6B]/5 border-2 border-dashed border-[#1D6B6B]/30 hover:border-[#1D6B6B] hover:bg-[#1D6B6B]/10 transition-all group"
                        >
                            <span className="font-mono text-2xl font-bold text-[#1D6B6B] tracking-wider">
                                {PROMO_CODE}
                            </span>
                            {copied ? (
                                <Check className="w-6 h-6 text-green-600" />
                            ) : (
                                <Copy className="w-6 h-6 text-[#7D8E95] group-hover:text-[#1D6B6B] transition-colors" />
                            )}
                        </button>

                        <SmallText className="text-[#7D8E95] text-center mt-3 block">
                            Click to copy, then enter in your account
                        </SmallText>
                    </div>

                    <PrimaryButton onClick={handleGetStarted} className="px-8 py-4 text-lg">
                        {user ? 'Apply Code Now' : 'Create Account & Use Code'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </PrimaryButton>
                </div>
            </div>

            {/* What You Get Section */}
            <div className="max-w-4xl mx-auto px-6 py-16">
                <div className="text-center mb-12">
                    <Heading as="h2" className="text-3xl text-[#1D6B6B] mb-4">
                        What You Get as a CoRegulater
                    </Heading>
                    <BodyText className="text-[#7D8E95] max-w-xl mx-auto">
                        Full access to premium tools designed specifically for ADHD emotional regulation
                    </BodyText>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    <div className="glass-card rounded-2xl p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-4">
                            <Sparkles className="w-7 h-7 text-[#FFBB98]" />
                        </div>
                        <Heading level={4} className="text-[#1D6B6B] mb-2">Premium Tools</Heading>
                        <SmallText className="text-[#7D8E95]">
                            EMDR, guided meditations, breathing patterns, body scans & more
                        </SmallText>
                    </div>

                    <div className="glass-card rounded-2xl p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-4">
                            <MessageCircle className="w-7 h-7 text-[#1D6B6B]" />
                        </div>
                        <Heading level={4} className="text-[#1D6B6B] mb-2">AI Guide</Heading>
                        <SmallText className="text-[#7D8E95]">
                            A supportive companion who understands ADHD and guides you to the right tools
                        </SmallText>
                    </div>

                    <div className="glass-card rounded-2xl p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-[#F48FB1]/20 flex items-center justify-center mx-auto mb-4">
                            <Heart className="w-7 h-7 text-[#F48FB1]" />
                        </div>
                        <Heading level={4} className="text-[#1D6B6B] mb-2">Relationship Hub</Heading>
                        <SmallText className="text-[#7D8E95]">
                            Tools for navigating ADHD in relationships with understanding
                        </SmallText>
                    </div>
                </div>

                {/* What We Ask */}
                <div className="glass-card-sage rounded-2xl p-8 mb-12">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/50 flex items-center justify-center flex-shrink-0">
                            <Users className="w-6 h-6 text-[#1D6B6B]" />
                        </div>
                        <div>
                            <Heading level={3} className="text-[#1D6B6B] mb-2">What We Ask in Return</Heading>
                            <BodyText className="text-[#344648] mb-4">
                                As a CoRegulater, we simply ask that you share quick feedback after using tools. It takes just one tap — and your insights directly shape how we improve.
                            </BodyText>
                            <div className="flex flex-wrap gap-3">
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60">
                                    <Star className="w-4 h-4 text-[#FFBB98]" />
                                    <SmallText className="text-[#344648]">Rate 5 tools</SmallText>
                                </div>
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60">
                                    <Check className="w-4 h-4 text-[#1D6B6B]" />
                                    <SmallText className="text-[#344648]">1-tap feedback</SmallText>
                                </div>
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60">
                                    <Heart className="w-4 h-4 text-[#F48FB1]" />
                                    <SmallText className="text-[#344648]">Shape the future</SmallText>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="text-center">
                    <Heading as="h3" className="text-2xl text-[#1D6B6B] mb-4">
                        Ready to Join?
                    </Heading>
                    <BodyText className="text-[#7D8E95] mb-6">
                        Your voice matters. Help us build something that truly works for ADHD minds.
                    </BodyText>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <PrimaryButton onClick={handleGetStarted} className="px-8">
                            {user ? 'Go to My Account' : 'Sign Up Free'}
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </PrimaryButton>
                        <SecondaryButton onClick={() => navigate('/')}>
                            Learn More About CoRegulate
                        </SecondaryButton>
                    </div>
                </div>
            </div>

            {/* Footer Note */}
            <div className="bg-[#1D6B6B]/5 py-8 px-6">
                <div className="max-w-2xl mx-auto text-center">
                    <SmallText className="text-[#7D8E95]">
                        CoRegulate is built by people who understand ADHD, for people navigating it every day. 
                        Your feedback isn't just appreciated — it's essential.
                    </SmallText>
                </div>
            </div>
        </div>
    );
};

export default PromoLanding;
